import { HttpBackend, HttpClient } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import {
  AppointmentRequest,
  ICurrentWeekParams,
  IEarliestAvailability,
  SearchRequest
} from 'avn/modules/features/service-list/search-page/service.interface';
import { Constants } from 'avn/modules/services/constants';
import { StoreService } from 'avn/modules/services/store.service';
import { Observable, catchError, throwError } from 'rxjs';

const BASE_URL = Constants.BASE_URL;
const BASE_URL_WITHOUT_API = Constants.BASE_URL_withoutAPI;

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly rawHttpClient: HttpClient;
  private readonly errorHandler: ErrorHandler;

  public constructor(
    private readonly httpClient: HttpClient,
    private readonly httpBackend: HttpBackend,
    private readonly store: StoreService,
    private readonly injector: Injector
  ) {
    this.rawHttpClient = new HttpClient(this.httpBackend);
    this.errorHandler = this.injector.get(ErrorHandler);
  }

  // HOME PAGE SERVICES

  public getAllAvailableBusiness(): Observable<unknown> {
    const endPoint = `/all-business-search-list/?v=${this.store.getRandomNumber()}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getPopularList(): Observable<unknown> {
    const endPoint = `/get-avaana-categories/?limit=15&v=${this.store.getRandomNumber()}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getAllServices(): Observable<unknown> {
    const endPoint = `/get-avaana-categories/?v=${this.store.getRandomNumber()}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getGeoCodingByAddress(address: string): Observable<unknown> {
    const endPoint = '/get-geocode/?address=' + encodeURI(address);
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public wellbeingBusinessesNearMe(
    searchRequest: SearchRequest,
    pageNo = 1,
    params?: string,
    orderby?: string
  ): Observable<unknown> {
    let endPoint = `/search-service/?page=${String(pageNo)}&v=${this.store.getRandomNumber(true)}`;
    if (params) {
      endPoint += `&${params}`;
    }
    if (orderby) {
      endPoint += `&order_by=${orderby}`;
    }
    return this.httpClient.post(BASE_URL + endPoint, searchRequest).pipe(catchError(this.handleError.bind(this)));
  }

  public symptomBusinessesNearMe(searchRequest: SearchRequest, pageNo = 1): Observable<unknown> {
    const endPoint = `/search-health-condition/?page=${String(pageNo)}&v=${this.store.getRandomNumber()}`;
    return this.httpClient.post(BASE_URL + endPoint, searchRequest).pipe(catchError(this.handleError.bind(this)));
  }

  // HEALTH HUB APIs

  public getFeaturedPosts(search?: string): Observable<unknown> {
    let endPoint = '?per_page=4';
    if (search) {
      endPoint += '&search=' + search;
    }
    return this.httpClient.get(Constants.BLOG_POST_API_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getCategoryById(id: number): Observable<unknown> {
    const endPoint = `/${String(id)}`;
    return this.httpClient
      .get(Constants.BLOG_CATEGORIES_API_URL + endPoint)
      .pipe(catchError(this.handleError.bind(this)));
  }

  public fetchAuthorById(id: number): Observable<unknown> {
    const endPoint = `/${String(id)}`;
    return this.httpClient.get(Constants.BLOG_USER_API_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }
  // getFeaturedPosts(): Observable<any> {
  //   const endPoint = '/wp-content/';
  //   const blog_url = { url: Constants.BLOG_URL + '/posts-ordered?_fields=author,category' };
  //   return this.httpClient.post(BASE_URL + endPoint, blog_url);
  // }

  public getAllSuburbLocationlist(): Observable<unknown> {
    const endPoint = `/business-categories-suburbs/?v=${this.store.getRandomNumber()}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public earliestDates(
    pracId: string,
    serviceId: string,
    appointmentDate: string,
    currentTime: string,
    timezone: string,
    bookingMode: boolean
  ): Observable<IEarliestAvailability> {
    let endPoint = `/slot/availability/${serviceId}/${pracId}/?v=${this.store.getRandomNumber(
      true
    )}&appointment_date=${appointmentDate}&current_time=${currentTime}&timezone=${timezone}`;
    if (bookingMode !== undefined || bookingMode !== null) {
      endPoint += `&online=${bookingMode}`;
    }
    return this.httpClient
      .get<IEarliestAvailability>(BASE_URL + endPoint)
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getAvailableTimes(params: ICurrentWeekParams): Observable<unknown> {
    let endPoint = '';
    if (params.practitioner_id === 'any') {
      endPoint = `/booking-slots/${String(params.service_id)}/?v=${this.store.getRandomNumber(true)}&start_date=${
        params.start_date
      }&end_date=${params.end_date}&current_time=${params.current_date}&timezone=${params.timezone}`;
    } else {
      endPoint = `/booking-slots/${String(params.service_id)}/${String(
        params.practitioner_id
      )}/?v=${this.store.getRandomNumber(true)}&start_date=${params.start_date}&end_date=${
        params.end_date
      }&current_time=${params.current_date}&timezone=${params.timezone}`;
    }
    if (params.booking_mode !== undefined || params.booking_mode !== null) {
      endPoint += `&online=${params.booking_mode}`;
    }
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getClassAvailableTimes(params: ICurrentWeekParams): Observable<unknown> {
    let endPoint = '';
    if (params.practitioner_id === 'any') {
      endPoint = `/class-booking-slots/${String(params.service_id)}/?v=${this.store.getRandomNumber(true)}&start_date=${
        params.start_date
      }&end_date=${params.end_date}&current_time=${params.current_date}`;
    } else {
      endPoint = `/class-booking-slots/${String(params.service_id)}/${String(
        params.practitioner_id
      )}/?v=${this.store.getRandomNumber(true)}&start_date=${params.start_date}&end_date=${
        params.end_date
      }&current_time=${params.current_date}`;
    }
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getPractitionersList(
    searchRequest: SearchRequest,
    pageNo = 1,
    online: boolean,
    params?: string,
    orderby?: string
  ): Observable<unknown> {
    let endPoint = `/practitioners-listing/?page=${String(pageNo)}&v=${this.store.getRandomNumber()}&online=${String(
      online
    )}`;
    if (params) {
      endPoint += `&${params}`;
    }
    if (orderby) {
      endPoint += `&order_by=${orderby}`;
    }
    const body = searchRequest;
    return this.httpClient.post(BASE_URL + endPoint, body).pipe(catchError(this.handleError.bind(this)));
  }

  public fetchBusinessServiceDetail(id: string): Observable<unknown> {
    const endPoint = `/fetch-business-service-detail/${id}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public fetchBusinessClassDetail(id: string): Observable<unknown> {
    const endPoint = `/fetch-business-class-detail/${id}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public createTemporaryAppointment(apointmentRequest: AppointmentRequest): Observable<unknown> {
    const endPoint = `/create-temporary-appointment-details/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.post(BASE_URL + endPoint, apointmentRequest).pipe(catchError(this.handleError.bind(this)));
  }

  public getTemporaryAppointment(tempId: string): Observable<unknown> {
    const endPoint = `/get-temporary-appointment-details/${tempId}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getActivePolicies(): Observable<unknown> {
    const endPoint = `consent/policies/active/?v=${this.store.getRandomNumber(true)}`;
    return this.rawHttpClient.get(BASE_URL_WITHOUT_API + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public updateTemporaryAppointment(tempId: string, data: Record<string, unknown>): Observable<unknown> {
    const endPoint = `/update-temporary-appointment-details/${tempId}/`;
    return this.httpClient.post(BASE_URL + endPoint, data).pipe(catchError(this.handleError.bind(this)));
  }

  public veriftUserEmail(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/verify-email/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public latestSendOtpToUser(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/send-code/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }
  public latestVerifyOtpToUser(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/verify-code/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }
  public latestResendOtpToUser(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/resend-code/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }
  public sendOtpToUser(tempId: string): Observable<unknown> {
    const endPoint = `/send-code/${tempId}/`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }
  public resendOtpToUser(tempId: string): Observable<unknown> {
    const endPoint = `/resend-code/${tempId}/`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }
  public verifyOtpToUser(tempId: string, params: Record<string, unknown>): Observable<unknown> {
    const endPoint = `/verify-code/${tempId}/`;
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public signUp(data: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/sign-up/';
    return this.httpClient.post(BASE_URL + endPoint, data).pipe(catchError(this.handleError.bind(this)));
  }

  public validateTemporaryAppointment(tempId: string): Observable<unknown> {
    const endPoint = `/validate-temporary-appointment-request/${tempId}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getAppointmentDetails(slug: string): Observable<unknown> {
    const endPoint = `/get-appointment-details/${slug}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getBookingDetails(slug: string): Observable<unknown> {
    const endPoint = `/appointment-detail/${slug}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getCustomerBookingDetails(slug: string): Observable<unknown> {
    const endPoint = `/customer-booking-detail/${slug}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public editJourney(data: Record<string, unknown>): Observable<unknown> {
    const endPoint = 'wellbeing/edit-journey/';
    return this.httpClient.post(BASE_URL_WITHOUT_API + endPoint, data).pipe(catchError(this.handleError.bind(this)));
  }
  public getWellbeingJourneysList(): Observable<unknown> {
    const endPoint = `wellbeing/journey-list/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL_WITHOUT_API + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getCategoryContent(url: string): Observable<unknown> {
    const endPoint = '/wp-content/';
    const blogUrl = { url: Constants.BLOG_CONTENT_API_URL + url };
    return this.httpClient.post(BASE_URL + endPoint, blogUrl).pipe(catchError(this.handleError.bind(this)));
  }

  public getCategoryFAQ(category: string): Observable<unknown> {
    const endPoint = 'faq-' + category;
    return this.httpClient.get(Constants.BLOG_CONTENT_API_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public getSearchResultSubUrb(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/search-result-suburb/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public rescheduleAppointment(params: Record<string, unknown>, uuid: string): Observable<unknown> {
    const endPoint = `/reschedule-appointment/${uuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public confirmRescheduleAppointment(params: Record<string, unknown>, uuid: string): Observable<unknown> {
    const endPoint = `/appointment-reschedule/${uuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public cancelAppointment(params: Record<string, unknown>, uuid: string): Observable<unknown> {
    const endPoint = `/cancel-appointment/${uuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public fetchMapData(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = `/practitioners-location-listing/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public fetchProviderDetails(uuid: string): Observable<unknown> {
    const endPoint = `/get-provider-detail/${uuid}/?v=${this.store.getRandomNumber()}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public checkValidSession(): Observable<unknown> {
    const endPoint = `/validate-token/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public fetchNotifications(): Observable<unknown> {
    const endPoint = `/get-notifications/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public verifyClaimNumber(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/check-claim-validity/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public assistedBooking(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/assisted-booking/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public fetchAmenities(): Observable<unknown> {
    const endPoint = `/fetch-amenities/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public cancelTempAppointment(tempUuid: string): Observable<unknown> {
    const endPoint = `/cancelled-temporary-appointment-details/${tempUuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.get(BASE_URL + endPoint).pipe(catchError(this.handleError.bind(this)));
  }

  public exitIntent(params: Record<string, unknown>): Observable<unknown> {
    const endPoint = '/update-drop-off/';
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public userExist(params: Record<string, unknown>, claimNumber: string, modality: string): Observable<unknown> {
    const endPoint = `/customer-preference/?claim_number=${claimNumber}&modality=${modality}`;
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  public patientIntakeForm(uuid: string, params: Record<string, unknown>): Observable<unknown> {
    const endPoint = `/submit-patient-intake-data/${uuid}/?v=${this.store.getRandomNumber(true)}`;
    return this.httpClient.post(BASE_URL + endPoint, params).pipe(catchError(this.handleError.bind(this)));
  }

  private handleError(error: unknown): Observable<never> {
    // Call the global error handler
    this.errorHandler.handleError(error);

    // Re-throw the error so it can be caught by the component's subscribe error handler
    return throwError(() => error);
  }
}
